/**
 * SiteTreeViewer — Tree visualization for site crawl results with coverage indicators.
 *
 * Shows a coverage progress bar at top and an indented tree of pages with
 * status dots, titles, and step count badges.
 *
 * When `selectionMode` is true (page selection gate), each node gets a checkbox
 * with Select All / Deselect All controls and a Confirm Selection button.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, LinearProgress, Chip, IconButton, Collapse, Checkbox, Button, Popper, Fade, Paper } from '@mui/material';
import {
  CheckCircle as PassedIcon,
  Error as FailedIcon,
  RadioButtonUnchecked as PendingIcon,
  PlayCircle as ExecutingIcon,
  SkipNext as SkippedIcon,
  AutoFixHigh as GeneratingIcon,
  CameraAlt as CameraIcon,
  Lock as LockIcon,
  VpnKey as KeyIcon,
  BrokenImage as NoImageIcon,
} from '@mui/icons-material';
import { SiteTree, SiteTreeNode, SiteTreeCoverage, SiteTreeNodeStatus } from '../types';
import { DiscoveredPage } from '../hooks/useTestingSession';

const PRIORITY_BADGE: Record<number, { label: string; color: 'error' | 'warning' | 'default' }> = {
  1: { label: 'Critical', color: 'error' },
  2: { label: 'Standard', color: 'warning' },
  3: { label: 'Low', color: 'default' },
};

interface SiteTreeViewerProps {
  siteTree: SiteTree | null;
  coverage: SiteTreeCoverage | null;
  nodeScreenshots?: Record<string, string>;
  // Page selection gate props
  selectionMode?: boolean;
  discoveredPages?: DiscoveredPage[];
  onConfirmSelection?: (nodeIds: string[]) => void;
}

const TreeNodeRow: React.FC<{
  node: SiteTreeNode;
  nodes: Record<string, SiteTreeNode>;
  nodeScreenshots?: Record<string, string>;
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggle?: (nodeId: string) => void;
  priorityMap?: Record<string, number>;
  categoryMap?: Record<string, string>;
}> = ({ node, nodes, nodeScreenshots, selectionMode, selectedIds, onToggle, priorityMap, categoryMap }) => {
  const [showScreenshot, setShowScreenshot] = useState(false);
  const [hoverAnchor, setHoverAnchor] = useState<HTMLElement | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pathLabel = (() => {
    try {
      const path = new URL(node.url).pathname;
      return node.title && node.title !== path ? node.title : path || '/';
    } catch {
      return node.title || node.url;
    }
  })();

  // Extract URL path for display under title in selection mode
  const urlPath = (() => {
    try {
      const u = new URL(node.url);
      return u.pathname + u.search;
    } catch {
      return node.url;
    }
  })();

  const screenshotData = nodeScreenshots?.[node.id];
  const priority = priorityMap?.[node.id];
  const badge = priority ? PRIORITY_BADGE[priority] : undefined;

  // Hover handlers for selection mode thumbnail preview
  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (!selectionMode) return;
    const target = e.currentTarget;
    hoverTimerRef.current = setTimeout(() => setHoverAnchor(target), 300);
  };
  const handleMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHoverAnchor(null);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          pl: node.depth * 2,
          py: 0.25,
          px: 1,
          '&:hover': { bgcolor: 'action.hover' },
          cursor: selectionMode ? 'pointer' : undefined,
        }}
        onClick={selectionMode && onToggle ? () => onToggle(node.id) : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {selectionMode ? (
          <Checkbox
            size="small"
            checked={selectedIds?.has(node.id) ?? false}
            onChange={() => onToggle?.(node.id)}
            onClick={(e) => e.stopPropagation()}
            sx={{ p: 0, mr: 0.25 }}
          />
        ) : (
          STATUS_ICON[node.test_status] || STATUS_ICON.pending
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontSize: '0.8rem',
              fontWeight: node.depth === 0 ? 600 : 400,
            }}
            title={node.url}
          >
            {pathLabel}
          </Typography>
          {/* Show URL path in selection mode to differentiate pages with same title */}
          {selectionMode && node.title && node.title !== urlPath && (
            <Typography
              variant="caption"
              noWrap
              sx={{
                display: 'block',
                fontSize: '0.65rem',
                color: 'text.disabled',
                lineHeight: 1.2,
              }}
            >
              {urlPath}
            </Typography>
          )}
        </Box>
        {badge && (
          <Chip
            label={badge.label}
            size="small"
            color={badge.color}
            sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.6rem', px: 0.5 } }}
          />
        )}
        {node.is_login_page && (
          <KeyIcon sx={{ fontSize: 13, color: 'warning.main' }} titleAccess="Login page" />
        )}
        {node.requires_auth && !node.is_login_page && (
          <LockIcon sx={{ fontSize: 13, color: 'info.main' }} titleAccess="Requires authentication" />
        )}
        {!selectionMode && node.screenshot_available && screenshotData && (
          <IconButton
            size="small"
            onClick={() => setShowScreenshot((prev) => !prev)}
            sx={{ p: 0.25 }}
            title="Toggle screenshot"
          >
            <CameraIcon sx={{ fontSize: 14, color: showScreenshot ? 'primary.main' : 'text.disabled' }} />
          </IconButton>
        )}
        {selectionMode && screenshotData && (
          <CameraIcon sx={{ fontSize: 13, color: 'text.disabled' }} titleAccess="Screenshot available — hover to preview" />
        )}
        {selectionMode && node.forms_count > 0 && (
          <Chip
            label={`${node.forms_count} form${node.forms_count > 1 ? 's' : ''}`}
            size="small"
            color="info"
            variant="outlined"
            sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.6rem', px: 0.5 } }}
          />
        )}
        {!selectionMode && node.steps_count > 0 && (
          <Chip
            label={
              node.test_status === 'passed' || node.test_status === 'failed'
                ? `${node.passed_count}/${node.steps_count}`
                : `${node.steps_count} steps`
            }
            size="small"
            color={
              node.test_status === 'passed'
                ? 'success'
                : node.test_status === 'failed'
                ? 'error'
                : 'default'
            }
            variant="outlined"
            sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.5 } }}
          />
        )}
      </Box>
      {/* Hover thumbnail preview (selection mode) */}
      {selectionMode && (
        <Popper
          open={Boolean(hoverAnchor)}
          anchorEl={hoverAnchor}
          placement="left-start"
          transition
          sx={{ zIndex: 1300 }}
          modifiers={[
            { name: 'offset', options: { offset: [0, 8] } },
            { name: 'preventOverflow', options: { boundary: 'viewport', padding: 16 } },
          ]}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <Paper
                elevation={12}
                sx={{
                  p: 1.5,
                  width: 480,
                  maxWidth: 'calc(100vw - 32px)',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                }}
              >
                {/* Page title */}
                <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ display: 'block', mb: 0.25, fontSize: '0.85rem' }}>
                  {node.title || pathLabel}
                </Typography>
                {/* URL */}
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 1, fontSize: '0.7rem' }}>
                  {node.url}
                </Typography>

                {/* Screenshot — larger preview */}
                {screenshotData ? (
                  <Box
                    component="img"
                    src={`data:image/png;base64,${screenshotData}`}
                    alt={`Preview of ${pathLabel}`}
                    sx={{
                      width: '100%',
                      maxHeight: 360,
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.100',
                      display: 'block',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 120,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <NoImageIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
                    <Typography variant="caption" color="text.disabled">
                      No preview available
                    </Typography>
                  </Box>
                )}

                {/* Page details row */}
                <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {node.elements_count > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                      <strong>{node.elements_count}</strong> elements
                    </Typography>
                  )}
                  {node.forms_count > 0 && (
                    <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'info.main' }}>
                      <strong>{node.forms_count}</strong> form{node.forms_count > 1 ? 's' : ''}
                    </Typography>
                  )}
                  {node.links_count > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                      <strong>{node.links_count}</strong> link{node.links_count > 1 ? 's' : ''}
                    </Typography>
                  )}
                  {node.is_login_page && (
                    <Chip label="Login Page" size="small" color="warning" variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                  {node.requires_auth && !node.is_login_page && (
                    <Chip label="Auth Required" size="small" color="info" variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                  {badge && (
                    <Chip label={badge.label} size="small" color={badge.color} variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>

                {/* Page description */}
                {node.page_description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.75, fontSize: '0.72rem', lineHeight: 1.4 }}
                  >
                    {node.page_description}
                  </Typography>
                )}
              </Paper>
            </Fade>
          )}
        </Popper>
      )}
      {/* Page description caption */}
      {!selectionMode && node.page_description && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            pl: node.depth * 2 + 3,
            pr: 1,
            pb: 0.25,
            fontSize: '0.7rem',
            lineHeight: 1.3,
          }}
        >
          {node.page_description}
        </Typography>
      )}
      {/* Expandable screenshot thumbnail */}
      {!selectionMode && (
        <Collapse in={showScreenshot}>
          {screenshotData && (
            <Box sx={{ pl: node.depth * 2 + 3, pr: 1, pb: 0.5 }}>
              <Box
                component="img"
                src={`data:image/png;base64,${screenshotData}`}
                alt={`Screenshot of ${pathLabel}`}
                sx={{
                  maxHeight: 150,
                  maxWidth: '100%',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
            </Box>
          )}
        </Collapse>
      )}
      {node.children_ids.map((childId) => {
        const child = nodes[childId];
        return child ? (
          <TreeNodeRow
            key={childId}
            node={child}
            nodes={nodes}
            nodeScreenshots={nodeScreenshots}
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onToggle={onToggle}
            priorityMap={priorityMap}
            categoryMap={categoryMap}
          />
        ) : null;
      })}
    </>
  );
};

const STATUS_ICON: Record<SiteTreeNodeStatus, React.ReactNode> = {
  passed: <PassedIcon sx={{ fontSize: 14, color: 'success.main' }} />,
  failed: <FailedIcon sx={{ fontSize: 14, color: 'error.main' }} />,
  executing: <ExecutingIcon sx={{ fontSize: 14, color: 'primary.main' }} />,
  generating: <GeneratingIcon sx={{ fontSize: 14, color: 'info.main' }} />,
  skipped: <SkippedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />,
  pending: <PendingIcon sx={{ fontSize: 14, color: 'text.disabled' }} />,
};

const SiteTreeViewer: React.FC<SiteTreeViewerProps> = ({
  siteTree,
  coverage,
  nodeScreenshots,
  selectionMode,
  discoveredPages,
  onConfirmSelection,
}) => {
  // Build maps from discoveredPages for priority/category badges
  const { priorityMap, categoryMap } = useMemo(() => {
    const pMap: Record<string, number> = {};
    const cMap: Record<string, string> = {};
    if (discoveredPages) {
      for (const page of discoveredPages) {
        if (page.cached_priority) pMap[page.node_id] = page.cached_priority;
        if (page.cached_category) cMap[page.node_id] = page.cached_category;
      }
    }
    return { priorityMap: pMap, categoryMap: cMap };
  }, [discoveredPages]);

  // Smart default: pre-select pages with forms (forms_count > 0) and priority-1 pages
  const defaultSelected = useMemo(() => {
    if (!siteTree) return new Set<string>();
    const ids = new Set<string>();
    for (const [nid, node] of Object.entries(siteTree.nodes)) {
      if (node.test_status === 'skipped') continue;
      if (node.forms_count > 0 || priorityMap[nid] === 1 || node.is_login_page) {
        ids.add(nid);
      }
    }
    // If nothing would be selected, select all
    if (ids.size === 0) {
      for (const [nid, node] of Object.entries(siteTree.nodes)) {
        if (node.test_status !== 'skipped') ids.add(nid);
      }
    }
    return ids;
  }, [siteTree, priorityMap]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Initialize selection when entering selection mode
  useEffect(() => {
    if (selectionMode && defaultSelected.size > 0) {
      setSelectedIds(new Set(defaultSelected));
    }
  }, [selectionMode, defaultSelected]);

  const toggleNode = (nodeId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const allNodeIds = useMemo(() => {
    if (!siteTree) return new Set<string>();
    return new Set(
      Object.entries(siteTree.nodes)
        .filter(([, n]) => n.test_status !== 'skipped')
        .map(([nid]) => nid)
    );
  }, [siteTree]);

  const selectAll = () => setSelectedIds(new Set(allNodeIds));
  const deselectAll = () => setSelectedIds(new Set());

  if (!siteTree || !siteTree.root_id) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Building site map...
        </Typography>
      </Box>
    );
  }

  const rootNode = siteTree.nodes[siteTree.root_id];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Selection mode header */}
      {selectionMode && (
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
            Select pages to test ({selectedIds.size}/{allNodeIds.size} selected)
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
            <Button
              size="small"
              variant="text"
              onClick={selectAll}
              sx={{ fontSize: '0.7rem', py: 0, minWidth: 'auto', textTransform: 'none' }}
            >
              Select All
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={deselectAll}
              sx={{ fontSize: '0.7rem', py: 0, minWidth: 'auto', textTransform: 'none' }}
            >
              Deselect All
            </Button>
          </Box>
        </Box>
      )}

      {/* Coverage bar */}
      {!selectionMode && coverage && (
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" fontWeight={600}>
              Coverage: {coverage.pages_tested}/{coverage.total_pages} pages ({coverage.coverage_pct}%)
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {coverage.pages_passed > 0 && (
                <Typography variant="caption" color="success.main">{coverage.pages_passed} passed</Typography>
              )}
              {coverage.pages_failed > 0 && (
                <Typography variant="caption" color="error.main">{coverage.pages_failed} failed</Typography>
              )}
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={coverage.coverage_pct}
            color={coverage.pages_failed > 0 ? 'warning' : 'success'}
            sx={{ height: 4, borderRadius: 2 }}
          />
        </Box>
      )}

      {/* Node count when no coverage yet (still crawling) */}
      {!selectionMode && !coverage && (
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <Typography variant="caption" fontWeight={600}>
            Site Map: {siteTree.total_nodes} page{siteTree.total_nodes !== 1 ? 's' : ''} discovered
          </Typography>
        </Box>
      )}

      {/* Tree */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 0.5 }}>
        {rootNode && (
          <TreeNodeRow
            node={rootNode}
            nodes={siteTree.nodes}
            nodeScreenshots={nodeScreenshots}
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onToggle={toggleNode}
            priorityMap={priorityMap}
            categoryMap={categoryMap}
          />
        )}
      </Box>

      {/* Confirm selection button */}
      {selectionMode && onConfirmSelection && (
        <Box sx={{ px: 1.5, pb: 1, pt: 0.5 }}>
          <Button
            variant="contained"
            size="small"
            fullWidth
            disabled={selectedIds.size === 0}
            onClick={() => onConfirmSelection(Array.from(selectedIds))}
            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
          >
            Confirm Selection ({selectedIds.size} page{selectedIds.size !== 1 ? 's' : ''})
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SiteTreeViewer;
